import React, { ReactElement, useContext } from "react";
import { ActionType, CompleteServicesStepPayload } from "../../../../AppDataProvider/actions";
import { AppContext, BookingData, Service, ServiceTier } from "../../../../AppDataProvider/AppDataProvider";
import { Button as BaseButton, List, ListItem } from "../../../../common/components";
import { corners, gap, marginBottom, marginTop, media, typography } from "../../../../common/styles";
import { BookingVisibilityType, ServiceNotBookableAction } from "../../../../GeneratedGraphQL";
import { getCurrencyDisplay, getDurationDisplay, requireBookingDataFields } from "../../../../utils";
import { sanitize } from "dompurify";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { PartRequired } from "../../../../utils/types";
import CustomerTranslations from "../../../../utils/CustomerTranslations";
type ServiceStepBookingData = PartRequired<BookingData, "organisation">;
type ServiceListProps = {
  services: Service[];
};
export function ServiceList(props: ServiceListProps): ReactElement {
  const {
    t,
    i18n
  } = useTranslation();
  const {
    translateEntity
  } = CustomerTranslations.useCustomerTranslation();
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  const {
    services
  } = props;
  requireBookingDataFields<ServiceStepBookingData>(appData.bookingData, ["organisation"]);
  const {
    organisation
  } = appData.bookingData;
  const {
    businessPhone,
    communicationFromEmail,
    serviceNotBookableAction,
    showPricesOnBookingTools
  } = organisation;
  const getPriceDisplay = (service: Service): React.ReactNode => {
    const {
      durations
    } = service;
    const tiers = service.tiers?.filter((tier): tier is ServiceTier => true);
    const prices: number[] = tiers && tiers.length ? tiers.flatMap(tier => tier.pricePoints.map(pricePoint => pricePoint.pricePerQuantity)) : durations.map(duration => duration.price ?? 0);
    const minPrice = Math.min(...prices);
    const isSinglePrice = [...new Set(prices)].length === 1;
    let minPriceDisplay = minPrice === 0 ? t("free") : getCurrencyDisplay(minPrice, organisation.currency, i18n.language);
    minPriceDisplay = durations.some(duration => duration.POA === true) ? "POA" : minPriceDisplay;
    const showOldPrice = !(tiers && tiers.length) && isSinglePrice && typeof durations[0].oldPrice === "number" && typeof durations[0].price === "number" && durations[0].oldPrice > durations[0].price;
    if (!isSinglePrice) return t("fromValue", {
      value: minPriceDisplay
    });
    if (showOldPrice) {
      const oldPrice = durations[0].oldPrice as number;
      const oldPriceDisplay = getCurrencyDisplay(oldPrice, organisation.currency, i18n.language);
      return <>
          <OldPrice>{oldPriceDisplay}</OldPrice>
          {minPriceDisplay}
        </>;
    }
    return minPriceDisplay;
  };

  /**
   * Renders the variant of the service select button
   * when a service has been marked as unavailable for booking
   */
  const getServiceUnavailableButton = () => {
    return <Button variant="primary" onClick={() => {
      if (serviceNotBookableAction === ServiceNotBookableAction.Call && businessPhone) {
        window.open("tel:::org_number".replace("::org_number", businessPhone));
      } else if (serviceNotBookableAction === ServiceNotBookableAction.Email && communicationFromEmail) {
        window.open("mailto:::org_email".replace("::org_email", communicationFromEmail));
      }
    }} data-sentry-element="Button" data-sentry-component="getServiceUnavailableButton" data-sentry-source-file="ServiceList.tsx">
        {serviceNotBookableAction === ServiceNotBookableAction.Call ? t("callUs") : t("emailUs")}
      </Button>;
  };
  const selectService = (options: {
    service: Service;
  }) => {
    const payload: CompleteServicesStepPayload = {
      updatedData: {
        service: options.service
      }
    };
    appDataDispatch({
      payload,
      type: ActionType.COMPLETE_SERVICES_STEP
    });
  };
  const imageCount = services.filter(service => !!service.image).length;
  return <List data-sentry-element="List" data-sentry-component="ServiceList" data-sentry-source-file="ServiceList.tsx">
      {services.map(service => {
      const {
        _id,
        durations,
        image,
        bookingVisibilityType
      } = service;
      const selectButtonLabel = bookingVisibilityType !== BookingVisibilityType.Request ? t("select") : t("request");
      const {
        name,
        description
      } = translateEntity({
        document: service,
        type: "service"
      });
      return <ListItem key={_id}>
            {image ? <Image src={image} alt={`${name}`} /> : imageCount > 0 && <NoImagePlaceholder />}
            <Content>
              <div>
                <Name>{name}</Name>
                {description && <Description dangerouslySetInnerHTML={{
              __html: sanitize(description, {
                ADD_ATTR: ["target"]
              })
            }} />}
              </div>
              <BottomContainer>
                <PriceAndDuration>
                  <Duration className="serviceDuration">
                    {getDurationDisplay(durations, t)}
                  </Duration>
                  {showPricesOnBookingTools && <Price className="servicePrice">
                      {getPriceDisplay(service)}
                    </Price>}
                </PriceAndDuration>

                {bookingVisibilityType === BookingVisibilityType.Unavailable ? getServiceUnavailableButton() : <Button variant="primary" onClick={() => {
              selectService({
                service
              });
            }}>
                    {selectButtonLabel}
                  </Button>}
              </BottomContainer>
            </Content>
          </ListItem>;
    })}
    </List>;
}
const Image = styled.img`
  height: auto;
  max-width: 20rem;
  flex: 1;
  min-width: 12.5rem;
  object-fit: cover;

  ${corners.m}

  @media ${media.mobile} {
    width: 100%;
    max-width: 100%;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    ${marginBottom.s}
  }
`;
const NoImagePlaceholder = styled(Image).attrs({
  as: "div"
})`
  width: 100%;
  align-self: stretch;
  background-color: ${({
  theme
}) => theme.brand.cssVars["--d-border"]};

  @media ${media.mobile} {
    display: none;
  }
`;
const Content = styled.div`
  flex: 2;
  align-self: stretch;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  row-gap: 1rem;
`;
const Name = styled.h2`
  ${typography.subtitle}
  ${marginTop.zero}
  ${marginBottom.s}
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const Description = styled.div`
  ${typography.body}
  color: ${({
  theme
}) => theme.brand.cssVars["--d-muted"]};
`;
const BottomContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;

  @media ${media.mobile} {
    flex-direction: column;
    align-items: stretch;
  }
`;
const PriceAndDuration = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  ${gap.m}

  ${typography.small}
  color: ${({
  theme
}) => theme.brand.cssVars["--d-muted"]};

  @media ${media.mobile} {
    ${marginBottom.s}
    align-items: baseline;
  }

  span {
    &::first-letter {
      text-transform: capitalize;
    }
  }
`;
const Duration = styled.span``;
const Price = styled.span`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  ${gap.s}
`;
const OldPrice = styled.span`
  color: ${({
  theme
}) => theme.brand.cssVars["--d-muted"]};
  text-decoration: line-through;
`;
const Button = styled(BaseButton)`
  min-width: 6.25rem;
`;
export default ServiceList;